/**
 * Utility functions for processing workflow execution data and artifacts.
 * Handles status checking, artifact filtering, and download option generation.
 */
import {
  WorkflowExecution,
  WorkflowExecutionStatus,
  WorkflowArtifact,
  WorkflowArtifactType,
  WorkflowArtifactFile,
  WorkflowArtifactFileType
} from '@amzn/finspace-service-typescript-client';
import { downloadS3URI } from './fileUtils';
import { logger } from './logger';

export interface DownloadOption {
  label: string;
  s3URI: string;
}

/**
 * Checks if workflow execution status allows downloads
 */
export const isDownloadEnabled = (status: WorkflowExecutionStatus | undefined): boolean => {
  return status === WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED;
};

/**
 * Extracts REPORT artifacts from workflow execution
 */
export const getReportArtifacts = (execution: WorkflowExecution | undefined): WorkflowArtifact[] => {
  if (!execution?.artifacts) {
    return [];
  }

  return execution.artifacts.filter(
      (artifact) => artifact.type === WorkflowArtifactType.REPORT
  );
};

/**
 * Generates download options from REPORT artifacts
 */
export const generateDownloadOptions = (execution: WorkflowExecution | undefined): DownloadOption[] => {
  const reportArtifacts = getReportArtifacts(execution);

  if (reportArtifacts.length === 0) {
    return [];
  }

  // For P0, there will only be one entry for the type REPORT
  const reportArtifact = reportArtifacts[0];

  if (!reportArtifact.files || reportArtifact.files.length === 0) {
    return [];
  }

  return reportArtifact.files
      .filter((file): file is WorkflowArtifactFile =>
          file.type !== undefined && file.s3URI !== undefined
      )
      .map((file) => ({
        label: file.type as string,
        s3URI: file.s3URI as string,
      }));
};

/**
 * Checks if download should be enabled based on execution status and available artifacts
 */
export const shouldEnableDownload = (execution: WorkflowExecution | undefined): boolean => {
  if (!isDownloadEnabled(execution?.status)) {
    return false;
  }

  const downloadOptions = generateDownloadOptions(execution);
  return downloadOptions.length > 0;
};

/**
 * Handles file download from S3 URI using proper S3 authentication
 */
export const downloadFile = async (s3URI: string): Promise<void> => {
  try {
    await downloadS3URI(s3URI);
  } catch (error) {
    logger.error('Failed to download file:', error as Error);
    throw error;
  }
};